import { api, queryClient } from '@/lib/api-client.ts'
import { LoaderFunctionArgs } from 'react-router-dom'
import {
  Project,
  ProjectStage,
  ProjectStageLocationsDataT,
  ProjectStageParticipationsDataT,
  ProjectStageParticipationsT,
} from '@/routes/settings/projectDetails/lib/types.ts'
import { getCurrentOrganization } from '@/lib/utils.ts'
import { Issue } from '@/routes/projectStages/issue/types/issue.ts'
import { AxiosResponse } from 'axios'
import handleApiErr from '@/lib/helpers/handleApiGerError.ts'
import { getOrganizationIssueTypes } from '@/routes/settings/issueTypes/actions.ts'
import { getOrganizationStatusGroups } from '@/routes/settings/statuses/actions.ts'
import { getOrganizationLabelGroups } from '@/routes/settings/labels/actions.ts'
import { getOrganization } from '@/routes/settings/organization/users/actions.ts'

export const projectQuery = (projectId: string | undefined) => ({
  queryKey: ['project', { id: projectId }],
  queryFn: () => getProject(projectId),
})

export const projectsStagesQuery = (projectStageId: string | undefined) => ({
  queryKey: ['projectStage', { id: projectStageId }],
  queryFn: () => getProjectStage(projectStageId),
})

export const projectsStageByIriQuery = (projectStageIri: string | undefined) => ({
  queryKey: ['currStageUsersQuery', { projectStageIri }],
  queryFn: () => getProjectStageByIri(projectStageIri),
})

export const orgProjectStagesQuery = () => ({
  queryKey: ['orgProjectStages'],
  queryFn: () => getOrganizationProjectStages(),
})

export const getCurrentOrganizationQuery = (organizationId: string | undefined) => ({
  queryKey: ['currentOrganization', { organizationId }],
  queryFn: () => getOrganization(),
})

export const loaderSingleProject = async ({ params }: LoaderFunctionArgs) => {
  if (!params.projectId || !params.projectStageId) {
    throw new Error('No project ID provided')
  }

  // Helper to safely fetch or return cached data
  const safeEnsureQueryData = async <T>(queryOptions: {
    queryKey: unknown[]
    queryFn: () => Promise<T>
  }): Promise<T | undefined> => {
    const cached = queryClient.getQueryData<T>(queryOptions.queryKey)
    if (cached !== undefined) return cached
    if (!navigator.onLine) return undefined
    try {
      return await queryClient.ensureQueryData(queryOptions)
    } catch {
      return undefined
    }
  }

  const safeFetch = async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
    if (!navigator.onLine) return undefined
    try {
      return await fn()
    } catch {
      return undefined
    }
  }

  try {
    const [issueTypes, statusGroups, labelGroups] = await Promise.all([
      safeFetch(() => getOrganizationIssueTypes({ projectId: params.projectId! })),
      safeFetch(() => getOrganizationStatusGroups({ projectId: params.projectId! })),
      safeFetch(() => getOrganizationLabelGroups({ projectId: params.projectId! })),
      safeEnsureQueryData(projectQuery(params.projectId)),
      safeEnsureQueryData(orgProjectStagesQuery()),
      safeEnsureQueryData(getProjectStageParticipationsQuery(params.projectStageId)),
      safeEnsureQueryData(getProjectStageLocationsQuery(params.projectStageId)),
    ])

    return {
      issueTypes,
      statusGroups,
      labelGroups,
      projectId: params.projectId,
      ...(params.projectStageId && { projectStageId: params.projectStageId }),
    }
  } catch (e) {
    // Check if this is a network-related error
    const isNetworkError =
      !navigator.onLine ||
      (e instanceof Error &&
        (e.message.toLowerCase().includes('network') ||
          e.message.toLowerCase().includes('failed to fetch') ||
          (e as { code?: string }).code === 'ERR_NETWORK'))

    if (isNetworkError) {
      return {
        issueTypes: undefined,
        statusGroups: undefined,
        labelGroups: undefined,
        projectId: params.projectId,
        ...(params.projectStageId && { projectStageId: params.projectStageId }),
      }
    }
    return handleApiErr(e)
  }
}
export const getProject = async (projectId: string | undefined) => {
  if (!projectId) return null

  const response = await api.get(`/projects/${projectId}`)
  return response.data as Project
}

export const getProjectStage = async (projectStageId: string | undefined) => {
  if (!projectStageId) return null

  const response = await api.get(`/project_stages/${projectStageId}`)
  return response.data as Project
}

export const getProjectStageByIri = async (projectStageIri: string | undefined) => {
  if (!projectStageIri) return null

  const response = await api.get(`${projectStageIri}`)
  return response.data as ProjectStage
}

export const getOrganizationProjectStages = async () => {
  const organizationId = getCurrentOrganization()?.organization?.id

  if (!organizationId) return null

  const response = await api.get(`/organization/${organizationId}/project_stages?page=1`)
  return response.data as Project
}

export const getOrganizationData = async (organizationId: string) => {
  const response = await api.get(`/organization/${organizationId}/project_stages?page=1`)
  return response.data as Project
}

export const editProjectStageAttachments = async (
  projectStage: Pick<ProjectStage, 'id'> & {
    attachments: (string | undefined)[]
  },
): Promise<AxiosResponse<Issue>> => {
  return api.patch(`/project_stages/${projectStage.id}`, projectStage)
}

export const getProjectStageParticipations = async (
  stageId: string,
): Promise<ProjectStageParticipationsT> => {
  const response: AxiosResponse<ProjectStageParticipationsDataT> = await api.get(
    `/project_stages/${stageId}/project_participations`,
  )
  const stageParticipations: ProjectStageParticipationsT = response.data[`hydra:member`]

  return stageParticipations
}

export const getProjectStageParticipationsQuery = (stageId: string) => ({
  queryKey: ['getProjectStageParticipations'],
  queryFn: () => getProjectStageParticipations(stageId),
})

export const getProjectStageLocations = async (
  stageId: string | undefined,
): Promise<ProjectStageLocationsDataT['hydra:member']> => {
  const response: AxiosResponse<ProjectStageLocationsDataT> = await api.get(
    `/project_stages/${stageId}/locations`,
  )
  return response.data[`hydra:member`] ?? []
}
export const getProjectStageLocationsQuery = (projectStageId: string | undefined) => ({
  queryKey: ['getProjectStageLocations', { projectStageId }],
  queryFn: () => getProjectStageLocations(projectStageId),
})

export const editProject = async (project: Partial<Project>): Promise<AxiosResponse<Project>> => {
  return api.patch(`/projects/${project.id}`, project)
}

export const editProjectStage = async (
  projectStage: Partial<ProjectStage>,
): Promise<AxiosResponse<ProjectStage>> => {
  return api.patch(`/project_stages/${projectStage.id}`, projectStage)
}

export const deleteLocation = async (locationId: number): Promise<AxiosResponse> => {
  return api.delete(`/locations/${locationId}`)
}

export const addLocation = async (
  location: Partial<ProjectStageLocationsDataT['hydra:member'][0]>,
): Promise<AxiosResponse> => {
  return api.post(`/locations`, location)
}

export const editLocation = async (
  location: Partial<ProjectStageLocationsDataT['hydra:member'][0]>,
): Promise<AxiosResponse> => {
  return api.patch(`/locations/${location.id}`, location)
}
